/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2019, Zumper
 * @description   rentable media selectors
 */

import type { Media, MediaImageLayout } from '@zcode/bizos'
import { MediaType } from '@zcode/enums'
import { memoizeSelector } from '@zcode/memoizers'
import { selectIframeUrl, selectVideoUrl } from '../../../media/selectors'
import { selectReelImage } from '../../../media/selectors/reels'

type SingleMediaTypeArray = {
  [mediaType in MediaType]: ReadonlyArray<Media & { mediaType: mediaType }>
}[MediaType]

export const selectIsSingleMediaType = memoizeSelector((media: readonly Media[] | undefined) => {
  if (!media?.length) {
    return false
  }

  const initMediaType = media[0].mediaType

  return media.every(({ mediaType }) => mediaType === initMediaType)
}) as (media: readonly Media[] | undefined) => media is SingleMediaTypeArray

export function selectSingleMediaType<M extends SingleMediaTypeArray>(media: M): M[0]['mediaType']
export function selectSingleMediaType(media: readonly Media[] | undefined): MediaType | null
export function selectSingleMediaType(media: readonly Media[] | undefined): MediaType | null {
  if (!selectIsSingleMediaType(media)) {
    return null
  }

  return media[0].mediaType
}

export function selectSingleMediaTypeLabel<M extends SingleMediaTypeArray>(
  media: M,
  mediaCount: 1
): M[0]['mediaType'] extends MediaType.IFRAME
  ? 'virtual tour'
  : M[0]['mediaType'] extends MediaType.VIDEO
    ? 'video'
    : 'photo'
export function selectSingleMediaTypeLabel<M extends SingleMediaTypeArray>(
  media: M,
  mediaCount: number
): M[0]['mediaType'] extends MediaType.IFRAME
  ? 'virtual tour' | 'virtual tours'
  : M[0]['mediaType'] extends MediaType.VIDEO
    ? 'video' | 'videos'
    : 'photo' | 'photos'
export function selectSingleMediaTypeLabel(
  media: readonly MediaImageLayout[],
  mediaCount: 1
): 'photo' | null
export function selectSingleMediaTypeLabel(
  media: readonly MediaImageLayout[],
  mediaCount: number
): 'photo' | 'photos' | null
export function selectSingleMediaTypeLabel(
  media: readonly Media[],
  mediaCount: 1
): 'photo' | 'video' | 'virtual tour' | null
export function selectSingleMediaTypeLabel(
  media: readonly Media[],
  mediaCount: number
): 'photo' | 'photos' | 'video' | 'videos' | 'virtual tour' | 'virtual tours' | null
export function selectSingleMediaTypeLabel(
  media: readonly Media[],
  mediaCount: number
): 'photo' | 'photos' | 'video' | 'videos' | 'virtual tour' | 'virtual tours' | null {
  if (!selectIsSingleMediaType(media)) {
    return null
  }

  const mediaType = selectSingleMediaType(media)
  const suffix = mediaCount > 1 ? 's' : ''
  const label =
    mediaType === MediaType.IFRAME
      ? 'virtual tour'
      : mediaType === MediaType.VIDEO
        ? 'video'
        : 'photo'

  return `${label}${suffix}`
}

export const selectDisplayMedia = memoizeSelector(
  (media: readonly Media[] | undefined, noSort?: boolean) => {
    if (!media?.length) {
      return null
    }
    const isOneType = selectIsSingleMediaType(media)
    const availableMedia = media.filter((m) => {
      if (m?.isVideo) {
        return !!selectVideoUrl(m)
      }
      if (m?.isIframe) {
        return !!selectIframeUrl(m)
      }
      if (m?.isImage && m?.isAvailable) {
        return !!m?.url
      }
      if (m?.isReel) {
        return !!selectReelImage(m.id)
      }

      return false
    })
    const displayMedia =
      isOneType || noSort
        ? availableMedia
        : availableMedia.sort((a, b) => a.mediaType - b.mediaType)

    return displayMedia
  }
)
